import { useState, useEffect } from "react";
import { getAllAdmins, createAdmin, deleteAdmin } from "../../axios/axiosFunctions";
import { DataGrid } from "@mui/x-data-grid";
import {
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { DeleteConfirmationModal } from "../../components/common";

const UsersAdmin = () => {
  const [admins, setAdmins] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchAdmins = async () => {
    const data = await getAllAdmins();
    if (data) {
      setAdmins(data.map((a) => ({
        id: a._id,
        firstName: a.firstName || "",
        lastName: a.lastName || "",
        email: a.email,
      })));
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setError("");
  };

  const handleCreate = async () => {
    setError("");
    if (!email || !password) {
      setError("Email and password are required.");
      return;
    }

    setLoading(true);
    const res = await createAdmin({ firstName, lastName, email, password });
    setLoading(false);

    if (!res.user) {
      setError(res.msg || "Error creating admin.");
      return;
    }

    resetForm();
    setOpenCreate(false);
    fetchAdmins();
  };

  const handleDeleteClick = (id) => {
    setAdminToDelete(id);
    setOpenDelete(true);
  };

  const confirmDelete = async () => {
    if (!adminToDelete) return;
    await deleteAdmin(adminToDelete);
    setAdmins(admins.filter((a) => a.id !== adminToDelete));
    setOpenDelete(false);
    setAdminToDelete(null);
  };

  const columns = [
    { field: "firstName", headerName: "First Name", flex: 1 },
    { field: "lastName", headerName: "Last Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1.5 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick(params.row.id);
          }}
          size="small"
          sx={{ color: "red" }}
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Typography variant="h3" fontWeight={500} color="secondary">
        Admins
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "50px",
          marginBottom: "50px",
        }}
      >
        <Typography variant="h4" fontWeight={400} color="secondary">
          Admin Users
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenCreate(true)}
        >
          Add Admin
        </Button>
      </Box>
      <DataGrid columns={columns} rows={admins} showToolbar />

      <Dialog
        open={openCreate}
        onClose={() => { resetForm(); setOpenCreate(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle variant="h6" color="primary">
          Create Admin
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                fullWidth
                size="small"
              />
            </Box>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              size="small"
              required
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              size="small"
              required
            />
            {error && (
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            )}
            <Box sx={{ display: "flex", justifyContent: "end", gap: 2, mt: 1 }}>
              <Button
                onClick={handleCreate}
                variant="contained"
                color="primary"
                disabled={loading}
              >
                {loading ? "Creating..." : "Create"}
              </Button>
              <Button
                variant="outlined"
                onClick={() => { resetForm(); setOpenCreate(false); }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <DeleteConfirmationModal
        openDialog={openDelete}
        closeDialog={() => { setOpenDelete(false); setAdminToDelete(null); }}
        title="Delete Admin"
        message="Are you sure you want to delete this admin?"
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default UsersAdmin;
